import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Card, Heading, Text, Select, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, useToast, Table, Thead, Tbody, Tr, Th, Td } from '@chakra-ui/react';
import { ArrowBackIcon, CheckIcon } from '@chakra-ui/icons';
import Axios from '../utils/httpClient';
import Layout from './Layout';
import { kpiSections } from './kpiData';
import { formatCompleteName } from '../utils/nameFormatter';

const PRIMARY = '#133654';

const RequirementsByParameter = () => {
  const { id } = useParams(); // parameter code
  const navigate = useNavigate();
  const toast = useToast();

  const [kafedras, setKafedras] = useState([]);
  const [selectedKafedra, setSelectedKafedra] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parameter, setParameter] = useState(null);
  const [darslarSoni, setDarslarSoni] = useState({}); // Store darslar soni for each teacher (KPI 1.1 only)
  const [sentRequirements, setSentRequirements] = useState({}); // Track which teachers already have requirements sent

  // Helper to build a stable localStorage key per parameter+kafedra
  const storageKey = (kafedraId) => `req_state_${String(id)}_${String(kafedraId || '')}`;

  // Find the parameter details
  useEffect(() => {
    if (id) {
      const allParameters = kpiSections.flatMap(s => s.items);
      const foundParam = allParameters.find(p => String(p.no) === String(id));
      setParameter(foundParam);
    }
  }, [id]);

  // Load kafedras
  useEffect(() => {
    setLoading(true);
    Axios().get('api/v1/account/kafedralar/')
      .then(async res => {
        const kafedraData = res?.data?.kafedralar || [];
        console.log('Loaded kafedras:', kafedraData);
        console.log('Full response:', res);
        
        // Remove duplicates by name (keep first occurrence)
        const uniqueKafedras = [];
        const seenNames = new Set();
        
        kafedraData.forEach(kafedra => {
          const normalizedName = kafedra.name.trim().toLowerCase();
          if (!seenNames.has(normalizedName)) {
            seenNames.add(normalizedName);
            uniqueKafedras.push(kafedra);
          }
        });
        
        console.log('Unique kafedras:', uniqueKafedras.length, 'out of', kafedraData.length);
        
        // Filter out kafedras with no teachers
        const kafedrasWithTeachers = [];
        
        for (const kafedra of uniqueKafedras) {
          try {
            const teacherRes = await Axios().get(`api/v1/account/kafedralar/${kafedra.id}/teachers/`);
            const teachers = teacherRes?.data?.teachers || [];
            
            if (teachers.length > 0) {
              kafedrasWithTeachers.push(kafedra);
            } else {
              console.log('Skipping empty kafedra:', kafedra.name);
            }
          } catch (e) {
            console.error('Error checking teachers for kafedra:', kafedra.name, e);
            // Include kafedra if we can't check (to avoid hiding valid ones)
            kafedrasWithTeachers.push(kafedra);
          }
        }
        
        console.log('Kafedras with teachers:', kafedrasWithTeachers.length, 'out of', uniqueKafedras.length);
        setKafedras(kafedrasWithTeachers);
      })
      .catch(e => {
        console.error('Error loading kafedras:', e);
        console.error('Kafedra loading error details:', e.response?.data, e.message);
        toast({
          title: 'Xato',
          description: 'Kafedralar ro\'yxatini yuklashda xatolik yuz berdi',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Load teachers and check existing requirements when kafedra is selected
  useEffect(() => {
    if (selectedKafedra) {
      setLoading(true);

      // Load teachers
      Axios().get(`api/v1/account/kafedralar/${selectedKafedra}/teachers/`)
        .then(res => {
          const teacherData = res?.data?.teachers || [];
          console.log('Loaded teachers:', teacherData);

          // Sort teachers alphabetically by name
          const sortedTeachers = teacherData.sort((a, b) => {
            const nameA = (a.teacher_name || '').toLowerCase();
            const nameB = (b.teacher_name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });

          setTeachers(sortedTeachers);

          // Initialize darslar soni for each teacher
          const initialDarslarSoni = {};
          const initialSentRequirements = {};

          teacherData.forEach(teacher => {
            initialDarslarSoni[teacher.teacher_id] = '';
            initialSentRequirements[teacher.teacher_id] = false; // Initially assume no requirements sent
          });

          setDarslarSoni(initialDarslarSoni);
          setSentRequirements(initialSentRequirements);

          // Try to hydrate from localStorage immediately (in case API call fails or is delayed)
          try {
            const cached = JSON.parse(localStorage.getItem(storageKey(selectedKafedra)) || '{}');
            if (cached && typeof cached === 'object') {
              if (cached.sentRequirements) setSentRequirements(prev => ({ ...prev, ...cached.sentRequirements }));
              if (cached.darslarSoni) setDarslarSoni(prev => ({ ...prev, ...cached.darslarSoni }));
            }
          } catch { }

          // Check existing requirements for ALL teachers in this kafedra for this parameter
          // This is more efficient than making individual calls for each teacher
          Axios().get(`api/v1/application/check-list/`)
            .then(async res => {
              const allApplications = res?.data?.applications || [];
              console.log('All applications for kafedra:', allApplications.length);

              // Filter for requirement applications (status=6) for this parameter
              let requirementApps = allApplications.filter(app =>
                app.parameter?.code === String(id) &&
                app.status === 6
              );

              console.log('Requirement applications found (check-list):', requirementApps.length);

              // Fallback: some requirement apps might not appear in check-list.
              // Query the generic list endpoint (apps created by current user/department head)
              if (requirementApps.length === 0) {
                try {
                  const listRes = await Axios().get('api/v1/application/list/');
                  const listApps = listRes?.data?.applications || [];
                  console.log('Apps from list endpoint:', listApps.length);
                  const extra = listApps.filter(app => app.parameter?.code === String(id) && app.status === 6);
                  console.log('Requirement applications from list endpoint:', extra.length);
                  requirementApps = extra;
                } catch (e) {
                  console.log('Fallback list endpoint failed:', e?.message);
                }
              }

              // Mark teachers who already have requirements and prefill darslar soni from requirement work
              const nextSent = { ...initialSentRequirements };
              const nextDars = { ...initialDarslarSoni };

              teacherData.forEach(teacher => {
                const reqApp = requirementApps.find(app => String(app.user?.id) === String(teacher.teacher_id));
                if (reqApp) {
                  nextSent[teacher.teacher_id] = true;
                  // Try to parse totalLessons from work JSON/text
                  try {
                    // Prefer JSON
                    if (reqApp.work && reqApp.work.trim().startsWith('{')) {
                      const parsed = JSON.parse(reqApp.work);
                      if (parsed?.totalLessons) nextDars[teacher.teacher_id] = String(parsed.totalLessons);
                    } else if (reqApp.work) {
                      // Fallback regex
                      const m = reqApp.work.match(/totalLessons\"?:\s*\"?(\d+)\"?/);
                      if (m && m[1]) nextDars[teacher.teacher_id] = m[1];
                    }
                  } catch { }
                }
              });

              setSentRequirements(prev => ({ ...prev, ...nextSent }));
              setDarslarSoni(prev => ({ ...prev, ...nextDars }));

              // Persist to localStorage for resilience across reload/login
              try {
                localStorage.setItem(storageKey(selectedKafedra), JSON.stringify({
                  sentRequirements: { ...initialSentRequirements, ...nextSent },
                  darslarSoni: { ...initialDarslarSoni, ...nextDars },
                }));
              } catch { }
            })
            .catch(e => {
              console.error('Error loading requirement applications:', e);
              // Even if we can't load requirements, we still want to show the teachers
            });
        })
        .catch(e => {
          console.error('Error loading teachers:', e);
          console.error('Teacher loading error details:', e.response?.data, e.message);
          toast({
            title: 'Xato',
            description: 'O\'qituvchilar ro\'yxatini yuklashda xatolik yuz berdi',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setTeachers([]);
      setDarslarSoni({});
      setSentRequirements({});
    }
  }, [selectedKafedra, id]);

  const handleDarslarSoniChange = (teacherId, value) => {
    setDarslarSoni(prev => {
      const next = { ...prev, [teacherId]: value };
      // Persist per-kafedra state
      try {
        const cached = JSON.parse(localStorage.getItem(storageKey(selectedKafedra)) || '{}');
        localStorage.setItem(storageKey(selectedKafedra), JSON.stringify({
          ...(cached || {}),
          darslarSoni: next,
          sentRequirements,
        }));
      } catch { }
      return next;
    });
  };

  const handleSubmit = async () => {
    console.log('Submit clicked, selected kafedra:', selectedKafedra);
    if (!selectedKafedra) {
      toast({
        title: 'Xato',
        description: 'Kafedra tanlanmadi',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      console.log('Processing teachers:', teachers);
      // Process requirements for each teacher
      let processedCount = 0;

      for (const teacher of teachers) {
        // Debug teacher data
        console.log('Processing teacher:', teacher);
        const teacherDarslarSoni = darslarSoni[teacher.teacher_id] || '';

        // Only process teachers who have darslar soni (meaningful requirements)
        const hasDarslarSoni = String(id) === '1.1' && teacherDarslarSoni.trim() !== '';

        if (!hasDarslarSoni) {
          console.log('Skipping teacher', teacher.teacher_id, 'because no darslar soni provided');
          continue; // Skip this teacher if no meaningful requirement data
        }

        // Prepare form data for this teacher
        const formData = new FormData();
        formData.append('kafedra_id', selectedKafedra);
        formData.append('parameter_code', id);
        formData.append('teacher_id', teacher.teacher_id);

        // Set meaningful requirement text based on what's provided
        let requirementText = '';
        if (String(id) === '1.1' && teacherDarslarSoni) {
          requirementText = `Darslar soni: ${teacherDarslarSoni}`;
        } else {
          requirementText = 'Talab belgilandi';
        }

        formData.append('requirement', requirementText);
        formData.append('points', 0);

        // For KPI 1.1, include darslar soni data in the work field so teacher can see it
        if (String(id) === '1.1' && teacherDarslarSoni) {
          const workData = {
            totalLessons: teacherDarslarSoni
          };
          formData.append('work', JSON.stringify(workData));
        }

        // Debug form data
        console.log('Sending form data for teacher:', teacher.teacher_id, {
          kafedra_id: selectedKafedra,
          parameter_code: id,
          teacher_id: teacher.teacher_id,
          requirement: requirementText,
          points: 0
        });

        // Send requirement for this teacher
        await Axios({ multipart: true }).post('api/v1/application/set-requirements-by-parameter/', formData);

        // Mark this teacher as having requirements sent and persist
        setSentRequirements(prev => {
          const next = { ...prev, [teacher.teacher_id]: true };
          try {
            const cached = JSON.parse(localStorage.getItem(storageKey(selectedKafedra)) || '{}');
            localStorage.setItem(storageKey(selectedKafedra), JSON.stringify({
              ...(cached || {}),
              sentRequirements: next,
              darslarSoni,
            }));
          } catch { }
          return next;
        });

        processedCount++;
      }

      if (processedCount > 0) {
        toast({
          title: 'Muvaffaqiyat',
          description: `Talablarni belgilash muvaffaqiyatli amalga oshirildi. ${processedCount} ta o'qituvchi uchun talablar saqlandi.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Ogohlantirish',
          description: 'Hech qanday talab belgilanmadi. Kamida bitta o\'qituvchi uchun darslar soni kiritilishi kerak.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }

      // Don't navigate back automatically, let user see the updated UI
    } catch (e) {
      console.error('Error setting requirements:', e);
      console.error('Full error details:', e.response?.data, e.message, e);
      toast({
        title: 'Xato',
        description: 'Talablarni belgilashda xatolik yuz berdi: ' + (e.response?.data?.detail || e.message || 'Unknown error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box p={4}>
        <Button
          onClick={() => navigate(-1)}
          mb={3}
          color="white"
          bg={PRIMARY}
          _hover={{ bg: PRIMARY }}
        >
          <ArrowBackIcon />
          <span style={{ marginLeft: 5 }}>Orqaga</span>
        </Button>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Heading size="md">
            O'qituvchilarni o'quv kontenti uchun talablarini belgilash
          </Heading>
        </Box>

        <Card style={{ padding: 16, marginBottom: 16 }}>
          <Heading size="sm" mb={4}>Kafedra tanlash</Heading>
          <Select
            placeholder="Kafedra tanlang"
            value={selectedKafedra}
            onChange={(e) => setSelectedKafedra(e.target.value)}
            isDisabled={loading}
          >
            {kafedras.map(kafedra => (
              <option key={kafedra.id} value={kafedra.id}>
                {kafedra.name}
              </option>
            ))}
          </Select>
        </Card>

        {selectedKafedra && (
          <Box>
            <Heading size="sm" mb={2}>
              O'qituvchilar ({teachers.length} ta)
            </Heading>

            {loading ? (
              <Text>Yuklanmoqda...</Text>
            ) : teachers.length === 0 ? (
              <Text>Bu kafedrada o'qituvchilar topilmadi</Text>
            ) : (
              <Box>
                <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                  <Table variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th width="80px" textAlign="center">№</Th>
                        <Th>O'qituvchi</Th>
                        {String(id) === '1.1' && <Th width="120px">Darslar soni</Th>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {teachers.map((t, index) => (
                        <Tr
                          key={t.teacher_id}
                          bg={sentRequirements[t.teacher_id] ? "green.50" : "white"}
                        >
                          <Td textAlign="center">{index + 1}</Td>
                          <Td>
                            <Text>{formatCompleteName(t.teacher_name)}</Text>
                          </Td>
                          {String(id) === '1.1' && (
                            <Td>
                              <NumberInput
                                value={darslarSoni[t.teacher_id] || ''}
                                onChange={(value) => handleDarslarSoniChange(t.teacher_id, value)}
                                size="sm"
                                min={0}
                              >
                                <NumberInputField />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                            </Td>
                          )}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                <Box display="flex" justifyContent="flex-end" mt={4}>
                  <Button
                    colorScheme="whatsapp"
                    bg="#25D366"
                    color="white"
                    _hover={{ bg: "#128C7E" }}
                    _active={{ bg: "#075E54" }}
                    onClick={handleSubmit}
                    isLoading={loading}
                    isDisabled={loading}
                  >
                    Talablarni saqlash
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default RequirementsByParameter;